import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@vaiu/ui/card";
import { Button } from "@vaiu/ui/button";
import { Badge } from "@vaiu/ui/badge";
import BrandLogo from '@/components/shared/BrandLogo';
import { useBrandThemeContext } from '@/context/BrandThemeContext';

const BrandTestPage: React.FC = () => {
  const { currentBrand, theme, switchBrand, getAvailableBrands } = useBrandThemeContext();
  const [urlParams, setUrlParams] = useState('');
  const [bodyClasses, setBodyClasses] = useState('');

  useEffect(() => {
    // Update URL params display
    setUrlParams(window.location.search);
    
    // Update body classes display
    setBodyClasses(document.body.className);
    
    // Log current state
    console.log('🎨 BrandTestPage - Current state:', {
      currentBrand,
      theme: theme?.name,
      urlParams: window.location.search,
      bodyClasses: document.body.className
    });
  }, [currentBrand, theme]);

  return (
    <div className="min-h-screen bg-gradient-to-br p-8">
      <div className="max-w-4xl mx-auto space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-2xl">Brand Theme Debug Page</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Current State */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h3 className="font-semibold mb-2">Current State</h3>
                <div className="space-y-2 text-sm">
                  <div><strong>Current Brand:</strong> {currentBrand}</div>
                  <div><strong>Theme Name:</strong> {theme?.name}</div>
                  <div><strong>Company:</strong> {theme?.companyName}</div>
                  <div><strong>Primary Color:</strong> 
                    <span className="ml-2 inline-flex items-center space-x-1">
                      <div 
                        className="w-4 h-4 rounded border"
                        style={{ backgroundColor: theme?.primaryColor }}
                      />
                      <span className="font-mono">{theme?.primaryColor}</span>
                    </span>
                  </div>
                </div>
              </div>
              
              <div>
                <h3 className="font-semibold mb-2">Technical Info</h3>
                <div className="space-y-2 text-sm">
                  <div><strong>URL Params:</strong> <code>{urlParams || 'none'}</code></div>
                  <div><strong>Body Classes:</strong> <code>{bodyClasses}</code></div>
                  <div><strong>Available Brands:</strong> {getAvailableBrands().join(', ')}</div>
                </div>
              </div>
            </div>

            {/* Logo Tests */}
            <div>
              <h3 className="font-semibold mb-4">Logo Tests</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="p-4">
                  <h4 className="text-sm font-medium mb-2">Small Logo</h4>
                  <BrandLogo size="sm" variant="default" />
                </Card>
                
                <Card className="p-4">
                  <h4 className="text-sm font-medium mb-2">Medium Logo</h4>
                  <BrandLogo size="md" variant="default" />
                </Card>
                
                <Card className="p-4">
                  <h4 className="text-sm font-medium mb-2">Large Logo with Tagline</h4>
                  <BrandLogo size="lg" variant="default" showTagline={true} />
                </Card>
              </div>
            </div>

            {/* Color Palette */}
            <div>
              <h3 className="font-semibold mb-4">Color Palette</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {theme && Object.entries(theme.brandColors).map(([key, color]) => (
                  <Card key={key} className="p-3">
                    <div className="flex items-center space-x-2">
                      <div 
                        className="w-8 h-8 rounded border"
                        style={{ backgroundColor: color }}
                      />
                      <div>
                        <div className="text-sm font-medium capitalize">{key}</div>
                        <div className="text-xs text-muted-foreground font-mono">{color}</div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>

            {/* Brand Switcher */}
            <div>
              <h3 className="font-semibold mb-4">Brand Switcher</h3>
              <div className="flex flex-wrap gap-2">
                {getAvailableBrands().map((brand) => (
                  <Button
                    key={brand}
                    variant={currentBrand === brand ? "default" : "outline"}
                    size="sm"
                    onClick={() => switchBrand(brand)}
                  >
                    {brand}
                    {currentBrand === brand && <Badge className="ml-2">Active</Badge>}
                  </Button>
                ))}
              </div>
            </div>

            {/* CSS Variables Test */}
            <div>
              <h3 className="font-semibold mb-4">CSS Variables Test</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <div className="p-3 rounded" style={{ backgroundColor: 'hsl(var(--primary))' }}>
                  <span className="text-primary-foreground text-sm">Primary</span>
                </div>
                <div className="p-3 rounded" style={{ backgroundColor: 'hsl(var(--secondary))' }}>
                  <span className="text-secondary-foreground text-sm">Secondary</span>
                </div>
                <div className="p-3 rounded" style={{ backgroundColor: 'hsl(var(--accent))' }}>
                  <span className="text-accent-foreground text-sm">Accent</span>
                </div>
                <div className="p-3 rounded" style={{ backgroundColor: 'hsl(var(--muted))' }}>
                  <span className="text-muted-foreground text-sm">Muted</span>
                </div>
              </div>
            </div>

            {/* URL Test Links */}
            <div>
              <h3 className="font-semibold mb-4">Test Links</h3>
              <div className="flex flex-wrap gap-2">
                <a href="?brand=vaiu" className="text-blue-600 hover:underline">Vaiu Theme</a>
                <a href="?brand=rbko" className="text-blue-600 hover:underline">RBKo Theme</a>
                <a href="?brand=bnp" className="text-blue-600 hover:underline">BNP Theme</a>
                <a href="?brand=payformyfood" className="text-blue-600 hover:underline">PayForMyFood Theme</a>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default BrandTestPage; 